import { useCallback, useEffect, useMemo, useState } from 'react';

const EMPTY = Object.freeze([]);
const empty = loading => ({ data: EMPTY, loading, loaded: false, error: null, fromCache: false });

/** Shared web/native read lifecycle. Platform subscriptions supply data, errors and snapshot metadata. */
export function useSnapshotRead({ identity, subscribe, enabled = true, retainOnRetry = false }) {
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([identity, enabled, attempt]);
  const active = enabled && identity != null;
  const [state, setState] = useState({ identity: null, key: null, ...empty(active) });
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  const updateData = useCallback(update => setState(previous => previous.key === key
    ? { ...previous, data: typeof update === 'function' ? update(previous.data) : update } : previous), [key]);
  useEffect(() => {
    if (!active) {
      setState({ identity, key, ...empty(false) });
      return undefined;
    }
    let current = true;
    setState(previous => ({ identity, key, ...(retainOnRetry && previous.identity === identity && previous.loaded
      ? { ...previous, key, loading: true, error: null, fromCache: true } : empty(true)) }));
    const receive = (data, error = null, metadata = {}) => {
      if (!current) return;
      setState({ identity, key, data: data || EMPTY, error,
        loaded: !error || Boolean(data?.length), loading: metadata.loading === true,
        fromCache: metadata.fromCache === true });
    };
    let unsubscribe;
    try { unsubscribe = subscribe(receive); }
    catch (error) { receive(EMPTY, error); }
    return () => { current = false; unsubscribe?.(); };
  }, [identity, key, active, subscribe, retainOnRetry]);
  return useMemo(() => {
    if (!active) return { ...empty(false), retry, updateData };
    if (state.key === key) return { ...state, retry, updateData };
    return { ...(retainOnRetry && state.identity === identity && state.loaded
      ? { ...state, loading: true, error: null, fromCache: true } : empty(true)), retry, updateData };
  }, [active, state, key, retainOnRetry, identity, retry, updateData]);
}
